/**
 * Detail drawer for one compliance requirement (Analysis tab).
 *
 * On desktop (>=lg) this is a plain <aside> beside the Compliance Matrix, NOT a
 * Radix Dialog: the table has to stay clickable while the drawer is open, so a
 * focus trap and an overlay are both wrong. Below lg the two-column layout does
 * not exist, so the same content rides in `ui/sheet.tsx` instead (spec A2).
 */

import { useState } from 'react'
import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { AssessmentBlock } from '@/components/extraction/AssessmentBlock'
import { CitationLink } from '@/components/extraction/CitationLink'
import { OwnerPicker } from '@/components/extraction/OwnerPicker'
import { PositionPill } from '@/components/extraction/PositionPill'
import { ProgressCell } from '@/components/extraction/ProgressCell'
import { RequirementDiscussion } from '@/components/extraction/RequirementDiscussion'
import { CoverageCell } from '@/components/requirement-coverage/CoverageCell'
import { ExpectationPill } from '@/components/requirement-coverage/ExpectationPill'
import { expectationConfig } from '@/components/requirement-coverage/coverage-status'
import { useIsDesktop } from '@/hooks/useIsDesktop'
import { effectiveProgress } from '@/lib/compliance-progress'
import { formatReqRef } from '@/lib/requirement-refs'
import type { CoverageStatus } from '@/lib/api'
import type {
  ExtractedComplianceItem,
  PositionValue,
  ResponseExpectation,
} from '@/types/extraction'

// ===================
// Types
// ===================

export interface RequirementDrawerProps {
  bidId: string
  /** `null` renders nothing — the host keeps the drawer mounted and passes the active row. */
  item: ExtractedComplianceItem | null
  onClose: () => void
  readOnly: boolean
  teamMembers: Array<{ id: string; userId: string; fullName: string | null; avatarUrl: string | null }>
  /** Needed by ProgressCell to derive progress from coverage. */
  coverageStatus: CoverageStatus | undefined
  onPositionChange: (itemId: string, position: PositionValue) => void
  onOwnerChange: (itemId: string, userId: string | null) => void
  onExpectationChange: (itemId: string, expectation: ResponseExpectation) => void
  onStatusChange: (itemId: string, status: string) => void
  onStatusHandBack: (itemId: string) => void
}

// ===================
// Component
// ===================

interface DrawerBodyProps {
  bidId: string
  item: ExtractedComplianceItem
  onClose: () => void
  readOnly: boolean
  teamMembers: RequirementDrawerProps['teamMembers']
  coverageStatus: RequirementDrawerProps['coverageStatus']
  onPositionChange: RequirementDrawerProps['onPositionChange']
  onOwnerChange: RequirementDrawerProps['onOwnerChange']
  onExpectationChange: RequirementDrawerProps['onExpectationChange']
  onStatusChange: RequirementDrawerProps['onStatusChange']
  onStatusHandBack: RequirementDrawerProps['onStatusHandBack']
  /** The sheet needs a Radix <SheetTitle> for its dialog label; the aside uses a plain <h2>. */
  titleAs?: 'h2' | 'sheet'
}

function DrawerBody({
  bidId,
  item,
  onClose,
  readOnly,
  teamMembers,
  coverageStatus,
  onPositionChange,
  onOwnerChange,
  onExpectationChange,
  onStatusChange,
  onStatusHandBack,
  titleAs = 'h2',
}: DrawerBodyProps) {
  const expectation = item.responseExpectation ?? 'narrative'
  const reqRef = formatReqRef(item.displayNumber)

  /**
   * The pinned footer the discussion portals its composer into. Held as state
   * rather than a ref so the child re-renders once the element exists — a ref
   * would still be null on the render that mounts the composer.
   */
  const [composerSlot, setComposerSlot] = useState<HTMLDivElement | null>(null)

  const titleClassName = 'text-sm font-medium'
  const title =
    titleAs === 'sheet' ? (
      <SheetTitle className={titleClassName}>{item.title}</SheetTitle>
    ) : (
      <h2 className={titleClassName}>{item.title}</h2>
    )

  return (
    <>
      {/* Head — fixed */}
      <div className="flex items-start justify-between gap-2 border-b border-border p-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-start gap-1.5">
            {item.criticality === 'mandatory' && (
              <Badge
                variant="destructive"
                dot={false}
                title="Mandatory"
                className="mt-0.5 px-1 py-0 text-xs font-medium"
              >
                M
              </Badge>
            )}
            {title}
          </div>
          <p className="text-meta">
            {reqRef ? <span className="tabular-nums">{reqRef} · </span> : null}
            {item.category}
            {item.refClause ? ` · ${item.refClause}` : ''}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          aria-label="Close details"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Body — scrolls */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {item.description && <p className="text-sm text-muted-foreground">{item.description}</p>}

        {/* Controls — label column + control, same widgets as the row */}
        <div className="space-y-2.5">
          <div className="flex items-center gap-3">
            <span className="w-28 shrink-0 text-meta">Can we comply?</span>
            <PositionPill
              position={item.position ?? 'unassessed'}
              positionSource={item.positionSource ?? 'ai'}
              assessedAt={item.positionAssessedAt ?? null}
              unassessedReason={item.unassessedReason ?? null}
              disabled={readOnly || !item.id}
              onChange={(next) => item.id && onPositionChange(item.id, next)}
            />
          </div>
          <div className="flex items-start gap-3">
            <span className="w-28 shrink-0 pt-1 text-meta">Where answered</span>
            <div className="flex flex-wrap items-center gap-1.5">
              <ExpectationPill
                value={expectation}
                readOnly={readOnly || !item.id}
                onChange={(next) => item.id && onExpectationChange(item.id, next)}
              />
              {expectation === 'narrative' && item.id ? (
                <CoverageCell
                  bidId={bidId}
                  requirementId={item.id}
                  requirementKind="compliance"
                  readOnly={readOnly}
                />
              ) : (
                expectationConfig[expectation].hintBeyondLabel && (
                  <span className="text-meta">{expectationConfig[expectation].hint}</span>
                )
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="w-28 shrink-0 text-meta">Owner</span>
            <OwnerPicker
              value={item.ownerId}
              teamMembers={teamMembers}
              disabled={readOnly}
              onChange={(userId) => item.id && onOwnerChange(item.id, userId)}
            />
          </div>
          {/* Post-award rows have no progress at all, so the row disappears. */}
          {effectiveProgress(item, coverageStatus).bucket !== null && (
            <div className="flex items-center gap-3">
              <span className="w-28 shrink-0 text-meta">Progress</span>
              <ProgressCell
                variant="drawer"
                item={item}
                coverageStatus={coverageStatus}
                readOnly={readOnly}
                onStatusChange={(status) => item.id && onStatusChange(item.id, status)}
                onHandBack={
                  (item.responseExpectation ?? 'narrative') === 'narrative' && item.id
                    ? () => onStatusHandBack(item.id!)
                    : undefined
                }
              />
            </div>
          )}
        </div>

        {/* AI assessment — a full-bleed tinted BAND, not a nested card. The
            drawer is already a card, so a second border + radius inside it is a
            box in a box; the negative margin cancels the body's p-4 so the wash
            runs edge to edge and the tint alone does the separating. Tinted =
            the model's read, white = the tender's own words. */}
        <div className="-mx-4 bg-muted/60 px-4 py-3">
          <AssessmentBlock bidId={bidId} item={item} readOnly={readOnly} />
        </div>

        {/* Source */}
        {item.sourceQuote && (
          <div className="space-y-1.5 border-t border-border pt-3">
            <span className="text-sm font-medium text-foreground">Source</span>
            <blockquote className="border-l-2 border-border pl-2.5 text-xs text-muted-foreground">
              &ldquo;{item.sourceQuote}&rdquo;
            </blockquote>
            <CitationLink
              page={item.sourcePage}
              documentId={item.sourceDocumentId ?? null}
              documentName={item.sourceDocument}
              sourceQuote={item.sourceQuote}
              sourceHighlightData={item.sourceHighlightData ?? null}
            />
          </div>
        )}

        {/* Discussion — a manual row that has never been saved has no id to
            hang a thread off yet. */}
        {item.id && (
          <div className="mt-2 pt-1">
            <RequirementDiscussion
              bidId={bidId}
              itemId={item.id}
              readOnly={readOnly}
              composerSlot={composerSlot}
            />
          </div>
        )}
      </div>

      {/* Pinned composer footer — filled by RequirementDiscussion's portal, and
          collapsed by `empty:hidden` when the viewer may not post. */}
      {item.id && (
        <div
          ref={setComposerSlot}
          className="shrink-0 border-t border-border p-3 empty:hidden"
        />
      )}
    </>
  )
}

export function RequirementDrawer({
  bidId,
  item,
  onClose,
  readOnly,
  teamMembers,
  coverageStatus,
  onPositionChange,
  onOwnerChange,
  onExpectationChange,
  onStatusChange,
  onStatusHandBack,
}: RequirementDrawerProps) {
  const isDesktop = useIsDesktop()

  if (!item) return null

  const shared = {
    bidId,
    item,
    onClose,
    readOnly,
    teamMembers,
    coverageStatus,
    onPositionChange,
    onOwnerChange,
    onExpectationChange,
    onStatusChange,
    onStatusHandBack,
  }

  if (!isDesktop) {
    return (
      <Sheet open onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" hideClose className="flex w-full flex-col p-0 sm:max-w-md">
          <DrawerBody {...shared} titleAs="sheet" />
        </SheetContent>
      </Sheet>
    )
  }

  // Width grows with the screen instead of sitting at a fixed 400px.
  // Floor 400px: at a 1200px viewport the table is ALREADY clipped (needs
  // ~756px, has ~667px), so there is nothing spare to hand over — the clamp
  // must never narrow the table further than it is today.
  // Cap 520px: past that the description paragraph runs longer than a
  // comfortable reading measure.
  return (
    <aside
      role="complementary"
      aria-label="Requirement details"
      className="flex max-h-full w-[clamp(400px,26vw,520px)] min-w-0 max-w-full shrink flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm"
    >
      <DrawerBody {...shared} />
    </aside>
  )
}
